import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '../../contexts/WalletContext'
import { getWebSocketConnection } from '../../hooks/useSmartWebSocket'
import {
  clearAffiliateMeCache,
  getAffiliateMe,
  getAffiliateCommissionHistory,
} from '../../services/affiliateService'
import { getReferralDisplayPromotionConfig, type UserReferralDisplaySettings } from '../../services/promotionConfigService'
import type { AffiliateMeData, AffiliateCommissionHistoryItem } from '../../services/affiliateService'
import { aggregateCommissionHistoryByDay } from '../../utils/affiliateHistoryUtils'

function formatHistoryDate(item: AffiliateCommissionHistoryItem): string {
  if (item.date) return item.date
  if (item.createdAt) {
    const d = new Date(item.createdAt)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
  }
  return '–'
}

const DEFAULT_DISPLAY_CONFIG: UserReferralDisplaySettings = {
  pageTitle: 'แนะนำเพื่อน',
  pageSubtitle: 'แชร์ลิงก์ของคุณเพื่อรับค่าคอมมิชชั่นจากยอดเล่นของสมาชิกที่คุณแนะนำ',
  withdrawButtonText: 'ถอนคอมมิชชั่น',
  shareSectionTitle: 'ลิงก์แนะนำ',
  linkLabel: 'ลิงก์สำหรับโปรโมท',
  copyButtonText: 'คัดลอก',
  shareDescription:
    'ลิงก์ช่วยแชร์รับ {{rate}} ฟรี (แค่ก๊อปปี้ลิงก์ไปแชร์ก็ได้เงินแล้ว) ยิ่งแชร์มากยิ่งได้มาก ท่านสามารถนำลิงก์ด้านล่างนี้ไปแชร์ในช่องทางต่างๆ ไม่ว่าจะเป็น เว็บไซต์ส่วนตัว, Blog, Facebook หรือ Social Network อื่นๆ หากมีการสมัครสมาชิกโดยคลิกผ่านลิงก์ของท่านเข้ามา ลูกค้าที่สมัครเข้ามาก็จะอยู่ภายใต้เครือข่ายของท่านทันที และหากลูกค้าภายใต้เครือข่ายของท่านมีการเดิมพันเข้ามา ทุกยอดการเดิมพันท่านจะได้รับส่วนแบ่งในการแนะนำ {{rate}} ทันทีโดยไม่มีเงื่อนไข',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบแนะนำเพื่อนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่พบรายการ',
  hideZeroCommissionSegments: true,
  promotionRoute: '/affiliate',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'แนะนำเพื่อน',
    badgeText: 'Referral',
    title: 'แนะนำเพื่อน',
    description: 'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
    buttonText: 'ไปหน้าแนะนำเพื่อน',
  },
}

function mergeDisplayConfig(input?: Partial<UserReferralDisplaySettings>): UserReferralDisplaySettings {
  return {
    ...DEFAULT_DISPLAY_CONFIG,
    ...(input || {}),
  }
}

function renderRateTemplate(text: string, rate: string): string {
  return String(text || '').replace(/\{\{\s*rate\s*\}\}/gi, rate || '0.00%')
}

function splitCommissionRate(rate: string): string[] {
  return String(rate || '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function isZeroCommissionSegment(segment: string): boolean {
  const match = String(segment || '').match(/(-?\d+(?:\.\d+)?)\s*%/)
  return match ? Number(match[1]) === 0 : false
}

const AffiliateOverallPage: React.FC = () => {
  const navigate = useNavigate()
  const { formatCurrency } = useWallet()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [affiliateDisabled, setAffiliateDisabled] = useState(false)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])
  const [displayConfig, setDisplayConfig] = useState<UserReferralDisplaySettings>(DEFAULT_DISPLAY_CONFIG)
  const refreshDisplayConfigRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshAffiliateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastDisplayEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const lastAffiliateEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const socketProxySubscribedRef = useRef(false)

  const loadDisplayConfig = useCallback(async () => {
    try {
      const res = await getReferralDisplayPromotionConfig()
      if (res?.success && res.data) {
        setDisplayConfig(mergeDisplayConfig(res.data.settings))
      } else {
        setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
      }
    } catch {
      setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
    }
  }, [])

  const loadAffiliateOverview = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    if (!options?.silent) {
      setLoading(true)
    }
    setError(null)

    if (options?.force) {
      clearAffiliateMeCache()
    }

    try {
      const [me, history] = await Promise.all([
        getAffiliateMe(options?.force === true),
        getAffiliateCommissionHistory(100),
      ])

      setAffiliateDisabled(false)
      setAffiliateData(me)
      setCommissionHistory(history)
    } catch (e: any) {
      const status = e?.status
      const msg = (e?.response?.message || e?.message || '') as string
      const isDisabled = status === 404 || (msg && msg.includes('not enabled'))

      if (isDisabled) {
        setAffiliateDisabled(true)
        setAffiliateData(null)
        setCommissionHistory([])

        if (!options?.silent) {
          Swal.fire({
            icon: 'info',
            title: displayConfig.disabledTitle,
            text: displayConfig.disabledMessage,
            confirmButtonText: 'ตกลง',
          })
        }
      } else {
        setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      }
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle])

  useEffect(() => {
    void loadDisplayConfig()
    void loadAffiliateOverview()

    return () => {
      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
        refreshDisplayConfigRef.current = null
      }
      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
        refreshAffiliateRef.current = null
      }
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  useEffect(() => {
    let cleanupSocket: (() => void) | undefined
    let intervalId: ReturnType<typeof setInterval> | undefined

    const isDuplicate = (
      ref: React.MutableRefObject<{ key: string; at: number }>,
      key: string
    ) => {
      const now = Date.now()
      if (ref.current.key === key && now - ref.current.at < 1200) {
        return true
      }
      ref.current = { key, at: now }
      return false
    }

    const handleDisplayConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        contentType?: string
        type?: string
        promotionType?: string
        timestamp?: string
        updatedAt?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      } | undefined

      const isReferralDisplayUpdate =
        detail?.type === 'referral_display_config_updated' ||
        detail?.promotionType === 'referral_display' ||
        detail?.content?.type === 'referral_display_config_updated' ||
        detail?.content?.promotionType === 'referral_display' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'referral_display' ||
          detail?.content?.promotionType === 'referral_display'
        ))

      if (!isReferralDisplayUpdate) return

      const eventKey = [
        detail?.type || '',
        detail?.promotionType || '',
        detail?.contentType || '',
        detail?.content?.type || '',
        detail?.content?.promotionType || '',
        detail?.timestamp || detail?.updatedAt || detail?.content?.updatedAt || '',
      ].join('|')

      if (isDuplicate(lastDisplayEventRef, eventKey)) return

      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
      }
      refreshDisplayConfigRef.current = setTimeout(() => {
        void loadDisplayConfig()
      }, 250)
    }

    const handleAffiliateConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        tenantId?: string
        enabled?: boolean
        timestamp?: string
      } | undefined

      const eventKey = [
        detail?.tenantId || '',
        String(detail?.enabled),
        detail?.timestamp || '',
      ].join('|')

      if (isDuplicate(lastAffiliateEventRef, eventKey)) return

      if (detail?.enabled === false) {
        setAffiliateDisabled(true)
        Swal.fire({
          icon: 'info',
          title: displayConfig.disabledTitle,
          text: displayConfig.disabledMessage,
          confirmButtonText: 'ตกลง',
        })
      } else if (detail?.enabled === true) {
        setAffiliateDisabled(false)
      }

      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
      }
      refreshAffiliateRef.current = setTimeout(() => {
        void loadAffiliateOverview({ silent: true, force: true })
      }, 250)
    }

    const subscribe = (socket: ReturnType<typeof getWebSocketConnection>) => {
      if (!socket || socketProxySubscribedRef.current) return
      socketProxySubscribedRef.current = true

      const forwardAffiliate = (data: {
        tenantId?: string
        enabled?: boolean
        updatedBy?: string
        timestamp?: string
        broadcastedAt?: string
      }) => {
        window.dispatchEvent(new CustomEvent('affiliate_config_updated', {
          detail: {
            tenantId: data?.tenantId,
            enabled: data?.enabled,
            updatedBy: data?.updatedBy,
            timestamp: data?.timestamp || data?.broadcastedAt,
          },
        }))
      }

      const forwardContent = (data: {
        type?: string
        tenantId?: string
        contentType?: string
        promotionType?: string
        timestamp?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      }) => {
        window.dispatchEvent(new CustomEvent('content_update', { detail: data }))
        if (typeof data?.type === 'string' && data.type.trim()) {
          window.dispatchEvent(new CustomEvent(data.type, { detail: data }))
        }
      }

      socket.on('affiliate_config_updated', forwardAffiliate)
      socket.on('content_update', forwardContent)

      return () => {
        socket.off('affiliate_config_updated', forwardAffiliate)
        socket.off('content_update', forwardContent)
        socketProxySubscribedRef.current = false
      }
    }

    const trySubscribe = () => {
      const socket = getWebSocketConnection()
      if (!socket) return
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
      }
      cleanupSocket = subscribe(socket)
    }

    window.addEventListener('content_update', handleDisplayConfigUpdate as EventListener)
    window.addEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)
    window.addEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)

    trySubscribe()
    if (!socketProxySubscribedRef.current) {
      intervalId = setInterval(trySubscribe, 1500)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
        refreshDisplayConfigRef.current = null
      }
      window.removeEventListener('content_update', handleDisplayConfigUpdate as EventListener)
      window.removeEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)
      window.removeEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)
      cleanupSocket?.()
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle, loadAffiliateOverview, loadDisplayConfig])

  useEffect(() => {
    const refreshFromServer = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void loadDisplayConfig()
    }

    const intervalId = setInterval(refreshFromServer, 30000)
    window.addEventListener('focus', refreshFromServer)
    document.addEventListener('visibilitychange', refreshFromServer)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', refreshFromServer)
      document.removeEventListener('visibilitychange', refreshFromServer)
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  const handleCopyLink = () => {
    if (affiliateDisabled || !affiliateData?.affiliateLink) return
    navigator.clipboard.writeText(affiliateData.affiliateLink)
    Swal.fire({
      icon: 'success',
      title: 'สำเร็จ!',
      text: 'คัดลอกลิ้งค์เรียบร้อยแล้ว',
      timer: 2000,
      showConfirmButton: false,
    })
  }

  const data = affiliateData ?? {
    commissionRate: '0.00%',
    referredMembers: 0,
    totalEarnings: 0,
    remainingEarnings: 0,
    affiliateLink: '',
    affiliateCode: '',
  }
  const shareDescription = renderRateTemplate(displayConfig.shareDescription, data.commissionRate || '0.00%')
  const commissionRateSegments = useMemo(() => {
    const segments = splitCommissionRate(data.commissionRate)
    const filteredSegments = displayConfig.hideZeroCommissionSegments
      ? segments.filter((segment) => !isZeroCommissionSegment(segment))
      : segments
    return filteredSegments.length > 0 ? filteredSegments : segments
  }, [data.commissionRate, displayConfig.hideZeroCommissionSegments])
  const canWithdraw = !affiliateDisabled && Number(data.remainingEarnings || 0) > 0
  const dailyCommissionHistory = useMemo(
    () => aggregateCommissionHistoryByDay(commissionHistory),
    [commissionHistory],
  )

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error && !affiliateDisabled) {
    return (
      <div className="text-center py-3">
        <p className="c-red">{error}</p>
      </div>
    )
  }

  const content = (
    <div className="text-center">
      <div className="row mx--1">
        <div className="col-12 px-1 mb-2">
          <div
            className="bgc-4 bdr-5 p-3 p-md-4 text-start overflow-hidden"
            style={{
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-start">
              <div>
                <div className="d-inline-flex align-items-center bgc-9 c-white px-3 py-1 bdr-10 fs-12 mb-3">
                  Affiliate Dashboard
                </div>
                <div className="fs-18 t-medium c-red">{displayConfig.pageTitle}</div>
                <div className="mt-1 fs-14 c-dark" style={{ opacity: 0.78, maxWidth: 720 }}>
                  {displayConfig.pageSubtitle}
                </div>
              </div>
              <div
                className="bgc-7 c-dark px-3 py-3 bdr-5 text-start w-100"
                style={{
                  maxWidth: 260,
                  boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="fs-12 t-medium text-uppercase mb-2" style={{ opacity: 0.72 }}>
                  ลิงก์แนะนำของคุณ
                </div>
                <div className="t-medium wrap" style={{ wordBreak: 'break-word' }}>
                  {data.affiliateCode || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mx--1">
        <div className="col-12 col-lg-6 px-1 mb-2">
          <div
            className="bgg-1 bdc-1 p-3 p-md-4 text-start h-100"
            style={{
              borderRadius: 18,
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.14)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div>
                <div className="fs-12 text-uppercase c-white mb-1" style={{ opacity: 0.88 }}>
                  ส่วนแบ่งรายได้
                </div>
                <div className="t-medium c-white">เรทคอมมิชชั่นที่ใช้งานอยู่</div>
              </div>
              <div className="bgc-4 c-dark px-3 py-2 bdr-10 fs-12">
                {commissionRateSegments.length || 1} รายการ
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {(commissionRateSegments.length > 0 ? commissionRateSegments : [data.commissionRate || '0.00%']).map((segment) => (
                <div
                  key={segment}
                  className="bgc-4 c-dark px-3 py-2 bdr-10 t-medium"
                  style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
                >
                  {segment}
                </div>
              ))}
            </div>
          </div>
        </div>

        {[
          {
            label: 'สมาชิกแนะนำ (คน)',
            value: data.referredMembers,
          },
          {
            label: 'รายได้ทั้งหมด (บาท)',
            value: formatCurrency(data.totalEarnings),
          },
          {
            label: 'รายได้คงเหลือ (บาท)',
            value: formatCurrency(data.remainingEarnings),
          },
        ].map((card) => (
          <div key={card.label} className="col-6 col-lg-2 px-1 mb-2">
            <div
              className="bgg-1 bdc-1 p-3 text-start h-100"
              style={{
                borderRadius: 18,
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)',
              }}
            >
              <div className="fs-12 c-white mb-2" style={{ opacity: 0.72 }}>
                {card.label}
              </div>
              <div className="t-medium c-white" style={{ fontSize: '1.12rem', lineHeight: 1.35 }}>
                {card.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      <a
        href="#"
        className="check-button bgg-4 mb-2 w-100"
        style={{
          borderRadius: 18,
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.14)',
          opacity: canWithdraw ? 1 : 0.55,
          pointerEvents: canWithdraw ? 'auto' : 'none',
          cursor: canWithdraw ? 'pointer' : 'not-allowed',
        }}
        onClick={(e) => {
          e.preventDefault()
          if (canWithdraw) navigate('/affiliate-withdrawal')
        }}
        aria-disabled={!canWithdraw}
      >
        <h5 className="t-medium c-white">
          <i className="fas fa-dollar-sign me-2"></i>
          {displayConfig.withdrawButtonText}
        </h5>
      </a>

      <div className="bdr-5 bgc-4 p-3 p-md-4 mb-2 text-start" style={{ boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)' }}>
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3 mb-3">
          <div>
            <div className="fs-18 t-medium c-red">
              <i className="fas fa-link me-2"></i>
              {displayConfig.shareSectionTitle}
            </div>
            <div className="fs-13 c-dark mt-1" style={{ opacity: 0.74 }}>
              {displayConfig.linkLabel}
            </div>
          </div>
          <div className="bgc-9 c-white px-3 py-2 bdr-10 fs-12">
            พร้อมแชร์ได้ทันที
          </div>
        </div>

        <div className="d-flex align-items-stretch" style={{ gap: 10, flexWrap: 'nowrap' }}>
          <div className="w-100 bgc-7 px-2 py-2 bdr-5">
            <input
              type="text"
              className="w-100 bgc-7 border-0 input-show c-dark"
              style={{ background: 'transparent' }}
              value={data.affiliateLink}
              readOnly
            />
          </div>

          <button
            className="check-button bgg-1 bdc-1 c-white px-4"
            style={{
              minWidth: 180,
              borderRadius: 14,
              flexShrink: 0,
            }}
            onClick={handleCopyLink}
          >
            <p>
              <i className="fas fa-copy me-2"></i>{displayConfig.copyButtonText}
            </p>
          </button>
        </div>
      </div>

      <div className="bdr-5 bgc-4 p-2 fs-14 mb-2" style={{ boxShadow: '0 12px 30px rgba(0, 0, 0, 0.14)' }}>
        <div className="d-flex justify-content-between align-items-center px-2 py-1 mb-2">
          <div className="text-start">
            <div className="fs-18 t-medium c-red">ประวัติคอมมิชชั่นล่าสุด</div>
          </div>
        </div>
        <div className="table-responsive w-100">
          <table className="table mb-0 table-striped table-bordered text-center">
            <thead className="table-dark">
              <tr>
                <th>วันที่</th>
                <th>ยอดเสียสมาชิก</th>
                <th>คอมมิชชั่น</th>
              </tr>
            </thead>
            <tbody>
              {dailyCommissionHistory.length === 0 ? (
                <tr>
                  <td colSpan={3} className="c-dark py-4">{displayConfig.historyEmptyText}</td>
                </tr>
              ) : (
                dailyCommissionHistory.map((item, index) => (
                  <tr key={index}>
                    <td>{formatHistoryDate(item)}</td>
                    <td>{formatCurrency(item.betAmount ?? 0)}</td>
                    <td>{formatCurrency(item.commission ?? 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className="bdr-5 bgc-4 c-dark p-3 p-md-4 mt-2 fs-14 text-start"
        style={{
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.14)',
        }}
      >
        {shareDescription}
      </div>
    </div>
  )

  if (affiliateDisabled) {
    return (
      <div className="position-relative">
        {content}
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.4)', pointerEvents: 'auto' }}
        >
          <span className="text-danger fw-bold fs-4" style={{ textShadow: '0 0 8px rgba(255,255,255,0.8)' }}>
            {displayConfig.disabledTitle}
          </span>
        </div>
      </div>
    )
  }

  return content
}

export default AffiliateOverallPage
